import type { Instance, SnapshotOrInstance } from "@gadgetinc/mobx-quick-tree";
import { action, types } from "@gadgetinc/mobx-quick-tree";
import { countBy, find, map, sortBy, uniq } from "lodash";
import { NominalContractSnapshot, ServerContract } from "../../ServerContract";
import type { ProblemsList } from "../../problems/ProblemsList";
import { ShopifyConfigProblems } from "../../problems/finders/ShopifyConfigProblems";
import { GadgetDomainModelClass, contentHash } from "../../utils";
import type { AnyShopifyAppConfig } from "../ShopifyConnectionConfig";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { INTERNAL_SHOPIFY_API_VERSION, apiDeprecationDate } from "./ShopifyApiVersion";
import { getShopifyModelApiIdentifierByModelKey } from "./apiIdentifiers";
import type { ShopifyBaseMetadataObject } from "./file-based-actions/shopifyConnectionMetadataSchema";

/**
 * See packages/state-trees/src/connections/ShopifyConnectionConfig.ts for documentation
 * on the different types of Shopify configurations
 */
export const ShopifyBaseConfig = <Name extends string, T extends AnyShopifyAppConfig>(name: Name, AppConfig: T) => {
  class BasicShopifyConfig extends GadgetDomainModelClass(name, {
    serverContract: types.optional(ServerContract, NominalContractSnapshot),
    skipDailySyncOnlyModelsSync: types.optional(types.boolean, false),
    enabledModelKeys: types.array(types.string),
    shopifyApiVersion: types.optional(types.frozen<ShopifyApiVersion>(), INTERNAL_SHOPIFY_API_VERSION),
  }) {
    get name() {
      return "Shopify";
    }
    get remoteIdentifier() {
      return this.key;
    }
    get defaultApiIdentifier() {
      return "shopify";
    }
    get dominionID() {
      return ShopifyDominion.id;
    }
    get apiDeprecationDate() {
      return apiDeprecationDate(this.shopifyApiVersion);
    }
    get appConfigsAsArray(): Instance<typeof AppConfig>[] {
      throw new Error("Method not implemented.");
    }
    get problems(): Instance<typeof ProblemsList> {
      return ShopifyConfigProblems(this as any);
    }
    getAppConfigByApiKey(apiKey: string): Instance<typeof AppConfig> | undefined {
      return find(this.appConfigsAsArray, (c) => c.apiKey == apiKey);
    }
    get countAppConfigsByApiKey(): { [apiKey: string]: number } {
      return countBy(this.appConfigsAsArray, "apiKey");
    }
    get allApiKeys() {
      return map(this.appConfigsAsArray, "apiKey");
    }
    get enabledModelKeysAsArray() {
      return Array.from(this.enabledModelKeys.values());
    }
    get contentHash() {
      const modelKeys = uniq(this.enabledModelKeys).slice().sort();
      return contentHash([
        this.type,
        ...modelKeys,
        ...sortBy(this.appConfigsAsArray, ["createdDate", "key"]).map((config) => config.contentHash),
      ]);
    }
    get baseMetadataObject(): ShopifyBaseMetadataObject {
      const enabledModels = this.enabledModelKeysAsArray.map((key) => getShopifyModelApiIdentifierByModelKey(key, this.shopifyApiVersion));
      return {
        apiVersion: this.shopifyApiVersion,
        enabledModels: sortBy(enabledModels, (modelKey) => modelKey.toLowerCase()),
      };
    }
    @action
    putAppConfig(_config: SnapshotOrInstance<typeof AppConfig>): Instance<typeof AppConfig> {
      throw new Error("Method not implemented.");
    }
    @action
    removeAppConfig(_configKey: string): boolean {
      throw new Error("Method not implemented.");
    }
    @action
    setEnabledModelKeys(modelKeys: string[]) {
      this.enabledModelKeys.replace(modelKeys);
    }
    @action
    removeEnabledModelKey(modelKey: string) {
      if (this.enabledModelKeysAsArray.includes(modelKey)) {
        this.enabledModelKeys.remove(modelKey);
      }
    }
    @action
    setEnabledModelKey(modelKey: string) {
      if (!this.enabledModelKeysAsArray.includes(modelKey)) {
        this.enabledModelKeys.push(modelKey);
      }
    }
    @action
    setShopifyApiVersion(apiVersion: ShopifyApiVersion) {
      this.shopifyApiVersion = apiVersion;
    }
    @action
    setSkipDailySyncOnlyModelsSync(value: boolean) {
      this.skipDailySyncOnlyModelsSync = value;
    }
  }
  return BasicShopifyConfig;
};
